/**
 * Add-ons — paid features that sit NEXT TO the plan, not inside it.
 *
 * An add-on is not one more `features.*` flag, for three reasons: it has a
 * price of its own, the invoice has to know WHEN it was switched on, and the
 * platform gym editor rewrites the whole `features` map from the plan defaults
 * on every plan change (`handlePlanChange`) — a paid switch stored there would
 * silently vanish on the next upgrade. So the state lives on
 * `gyms/{gymId}.addons.<key>` and the history on `gyms/{gymId}/addonLog`,
 * both written only by the `setGymAddon` callable.
 *
 * One copy for the client (admin panel, platform editor), the `api` codebase
 * (`setGymAddon`), the `email` codebase (quarterly invoice; the monthly recap
 * and the yearly Wrapped Spraywall block, via `addonActiveInWindow`) and the
 * `mcp` codebase (its per-call add-on gate).
 *
 * The frame serves two add-ons today: `mcp` and `sprayWall` (the Spraywall
 * add-on is described in docs/spec/21-spraywall.md).
 */
import { toMillis } from "./toMillis.js";
import { parseQuarterLabel } from "./billing.js";

/**
 * The catalogue. `billing: "flatQuarterly"` = one fixed NET fee per quarter,
 * per currency, read from `platform/pricing.addons.<key>.prices`.
 */
export const ADDONS = {
  mcp: { key: "mcp", billing: "flatQuarterly" },
  sprayWall: { key: "sprayWall", billing: "flatQuarterly" },
};

/** Is the add-on switched on for this gym right now? Anything but a literal `true` is off. */
export function isAddonActive(gym, key) {
  return gym?.addons?.[key]?.active === true;
}

/**
 * The quarter as a half-open UTC millisecond window `[start, end)`.
 *
 * UTC on purpose, like `monthKey`: the quarterly invoice picks its MAU band
 * from UTC month keys, so the add-on charge answers "was it on in this
 * quarter" against the same calendar. Accepts the `{ year, quarter }` shape
 * `parseQuarterLabel` / the e-mail package's `previousQuarter` return, or a
 * bare "YYYY-Qn" label. Null when the input names no quarter.
 */
function quarterWindow(quarter) {
  const q = typeof quarter === "string" ? parseQuarterLabel(quarter) : quarter;
  const year = Number(q?.year);
  const n = Number(q?.quarter);
  if (!Number.isInteger(year) || !Number.isInteger(n) || n < 1 || n > 4) return null;
  const startMonth0 = (n - 1) * 3;
  return { start: Date.UTC(year, startMonth0, 1), end: Date.UTC(year, startMonth0 + 3, 1) };
}

/**
 * Was the add-on active on ANY day of the window? Derived from the on/off log
 * alone, including the state carried in from before the window (switched on
 * last year and never off → active all quarter). The carried-in state is the
 * state AT the first instant of the window, so an "off" stamped exactly at
 * the quarter start means it was not on in that quarter.
 *
 * The log is sorted by time here rather than trusted to arrive sorted — a
 * Firestore query ordered by `at` is the expected input, but a reorder would
 * otherwise flip the carried-in state. Entries at the same instant keep their
 * input order (stable sort).
 */
function activeDuringWindow(entries, win) {
  const sorted = entries
    .map((e, i) => ({ at: toMillis(e.at), action: e.action, i }))
    .sort((a, b) => (a.at - b.at) || (a.i - b.i));
  let activeAtStart = false;
  for (const e of sorted) {
    if (e.at > win.start) break;
    if (e.action === "on") activeAtStart = true;
    else if (e.action === "off") activeAtStart = false;
  }
  if (activeAtStart) return true;
  // Off at the start: charged only if it was switched ON inside the window.
  // (An "off" inside the window is only possible after an "on" — already
  // covered by one of the two branches.)
  return sorted.some((e) => e.action === "on" && e.at >= win.start && e.at < win.end);
}

/**
 * Was the add-on `key` active on ANY day of the window `[startMs, endMs)`?
 *
 * The same rule as the quarterly fee (`activeDuringWindow`), for a window the
 * caller picks. Why it exists: the monthly recap (a month) and the yearly
 * Wrapped (a year) ask "was it on during the window" for the Spraywall block
 * (D66), and `gym.addons.<key>.since` cannot answer that — it keeps only the
 * LAST switch-on. A gym that switched the add-on on in 2025 and off in the
 * middle of the window still counts; one that never had it active in the window
 * does not.
 *
 * The caller chooses the window and passes it in UTC milliseconds (the recap
 * month and the Wrapped year are UTC windows, like the invoice's quarter).
 * `addonLog` is the gym's `addonLog` entries (`{ addon, action, at }`, `at`
 * any shape `toMillis` reads); entries of other add-ons are ignored.
 *
 * False for a non-array log, no entry of `key`, non-finite bounds, or
 * `endMs <= startMs`.
 */
export function addonActiveInWindow(addonLog, key, startMs, endMs) {
  if (!Array.isArray(addonLog) || !Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return false;
  const entries = addonLog.filter((e) => e && e.addon === key);
  if (entries.length === 0) return false;
  return activeDuringWindow(entries, { start: startMs, end: endMs });
}

/**
 * The add-on charges from the CURRENT switches (`gym.addons.<key>.active`),
 * `[{ key, price }]` — a live estimate for the quarter in progress, for a
 * reader that cannot see the `addonLog` (it is gated on
 * `platform.gym.editAddons`). An add-on switched on now is on during this
 * quarter, so every row here IS charged; what the estimate cannot see is one
 * switched OFF earlier in the quarter. The invoice itself uses
 * `addonQuarterCharges`. Missing price → 0, same as there.
 */
export function activeAddonCharges(gym, pricing, currency) {
  const cur = currency || "HUF";
  return Object.keys(ADDONS)
    .filter((key) => isAddonActive(gym, key))
    .map((key) => {
      const price = pricing?.addons?.[key]?.prices?.[cur];
      return { key, price: typeof price === "number" && Number.isFinite(price) ? price : 0 };
    });
}

/**
 * The add-on charges for one gym and one quarter: `[{ key, price }]`, one row
 * per catalogue add-on that was active on ANY day of the quarter. No
 * proration — the same "highest state in the period" rule as the quarterly
 * MAU peak.
 *
 * `pricing` is the `platform/pricing` doc; `addonLog` the gym's
 * `addonLog` entries (`{ addon, action: "on"|"off", at }`, `at` any shape
 * `toMillis` reads). An add-on that was active but has no price in this
 * currency is still listed, at 0 — the invoice row then says it was on.
 * Empty list for no log, no active add-on, or an unreadable quarter.
 */
export function addonQuarterCharges(pricing, addonLog, quarter, currency) {
  const win = quarterWindow(quarter);
  if (!win || !Array.isArray(addonLog) || addonLog.length === 0) return [];
  const cur = currency || "HUF";
  const charges = [];
  for (const key of Object.keys(ADDONS)) {
    const entries = addonLog.filter((e) => e && e.addon === key);
    if (entries.length === 0 || !activeDuringWindow(entries, win)) continue;
    const price = pricing?.addons?.[key]?.prices?.[cur];
    charges.push({ key, price: typeof price === "number" && Number.isFinite(price) ? price : 0 });
  }
  return charges;
}
